import React, { FC, ComponentType } from "react";
import { Portal } from "@UI/Components/Portal/Portal";
import withProviders from "@App/hocs/withProviders";
import { cacheQueryData } from "@Libs/DataLayer/services/dataLayer";
import widgetRegistry from "../../config/registry";
import { IWidgetPropTypes, WidgetRegistry } from "@Widgets/types";

interface IWidgetConfig {
  Component: ComponentType;
  props: object;
  mapPropsToDataLayer?: WidgetRegistry[keyof IWidgetPropTypes]["mapPropsToDataLayer"];
  el: HTMLElement;
}

const getComponents = (reactComponentTargets: HTMLElement[]): IWidgetConfig[] =>
  reactComponentTargets
    .map((el) => {
      const reactClass = el.getAttribute("data-react-component");
      const props = JSON.parse(el.getAttribute("data-react-props"));
      const { Component, mapPropsToDataLayer } = widgetRegistry[reactClass];

      if (!Component) {
        return null;
      }

      const widget: IWidgetConfig = {
        Component,
        props,
        el,
        mapPropsToDataLayer,
      };
      return widget;
    })
    .filter((el) => !!el);

const WidgetRenderer: FC = () => {
  const reactComponentTargets = Array.prototype.slice.call(
    document.querySelectorAll("[data-react-component]")
  );

  const components = getComponents(reactComponentTargets);

  const renderComponent = (component: IWidgetConfig, index: number) => {
    const { Component, props, el, mapPropsToDataLayer } = component;

    if (mapPropsToDataLayer) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const queries = mapPropsToDataLayer(props as { [key: string]: any });
      cacheQueryData(...queries);
    }

    el.setAttribute("data-react-widget-loaded", "true");

    return (
      <Portal
        key={`${el.getAttribute("data-react-component")}-${index}`}
        target={el}
      >
        <Component {...props} />
      </Portal>
    );
  };

  return <>{components.map(renderComponent)}</>;
};

export default withProviders(WidgetRenderer);
